// 直接引具体目录而非 components barrel：barrel 会把全部组件（含
// AgeVerification / AgreementConfirmation 及其 SCSS）拉进 App 的静态闭包，
// Vite 随之把这些 CSS 提升到首屏 MainShell.css。这里只需要 AppRouter。
import AppRouter from "./components/AppRouter";
import { BatchBonusClaimGate } from "./components/BatchBonusClaim/BatchBonusClaimGate";
import "./index.css";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { initScreenWidth } from "./utils/screenWidth";
import { useFavoriteGames } from "./hooks/useFavoriteGames";
import { useIOSKeyboardFix } from "./hooks/useIOSKeyboardFix";
import { useInviteCodeUrlPersist } from "./hooks/useInviteCodeUrlPersist";
import { useRecallTracking } from "./hooks/useRecallTracking";
import { useUserDataFlattened } from "@/modules/user/stores";
import { PopupQueueProvider } from "./contexts/PopupQueueContext";
import { FloatingIconsProvider } from "./contexts/FloatingIconsContext";
import { useCustomerServices } from "./modules/homeDisplay/hooks";
import { useGlobalValue } from "./stores/GlobalState";
import BlockedDevicePage from "./pages/BlockedDevice";
import { SilentErrorBoundary } from "./components/ChunkLoadBoundary/ChunkLoadBoundary";
import "./utils/imageReloadManager";
import { useChatMountRequested } from "./components/chatMount";
import { useStartupData } from "./modules/startup/stores";
import { resolvePrerequisiteFlag } from "./config/popupConfig";

// LCP/LT 优化：把非首屏关键的弹窗 / Push 等组件延后挂载。
//
// 旧实现只用 requestIdleCallback（3s 兜底），但 idle 判定的是"主线程空闲"——首屏
// 2~4s 恰恰因为在等网络而空闲，闸门于是在骨架屏还没填上时就打开，把一批弹窗接口
// 和客服 SDK 塞进了首屏关键窗口（实测 idle 在 2852ms 触发，游戏区 4177ms 才好）。
//
// 现在改为：先等 load（首屏资源基本落地）→ 再等一次 idle → 且带硬超时兜底，
// 保证最迟一定放行。客服 SDK 则完全移出本闸门，改为点客服才挂载（见 components/chatMount）。
type IdleRequestCallback = (deadline: { didTimeout: boolean }) => void;
type RequestIdleCallback = (
  cb: IdleRequestCallback,
  opts?: { timeout: number }
) => number;
type CancelIdleCallback = (handle: number) => void;

function useDeferredMount(hardTimeoutMs = 8000): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;

    let done = false;
    let idleHandle: number | undefined;
    let softTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const trigger = () => {
      if (done) return;
      done = true;
      setReady(true);
    };

    const ric = (
      window as unknown as { requestIdleCallback?: RequestIdleCallback }
    ).requestIdleCallback;
    const cic = (
      window as unknown as { cancelIdleCallback?: CancelIdleCallback }
    ).cancelIdleCallback;

    // 硬兜底：无论 load / idle 是否到达，最迟 hardTimeoutMs 一定放行。
    // 这条是"弹窗永远不弹"的保险，不能去掉。
    const hardTimeoutId = setTimeout(trigger, hardTimeoutMs);

    const afterLoad = () => {
      if (done) return;
      // load 之后再让一次 idle，避开收尾的图片解码；仍有 hardTimeoutId 兜底
      if (typeof ric === "function") {
        idleHandle = ric(trigger, { timeout: 1500 });
      } else {
        softTimeoutId = setTimeout(trigger, 300);
      }
    };

    if (document.readyState === "complete") {
      afterLoad();
    } else {
      window.addEventListener("load", afterLoad, { once: true });
    }

    return () => {
      done = true;
      clearTimeout(hardTimeoutId);
      window.removeEventListener("load", afterLoad);
      if (idleHandle !== undefined && typeof cic === "function") {
        cic(idleHandle);
      }
      if (softTimeoutId !== undefined) {
        clearTimeout(softTimeoutId);
      }
    };
  }, [ready, hardTimeoutMs]);

  return ready;
}

const LeftBottomFloatingIcons = lazy(() =>
  import("./components/LeftBottomFloatingIcons").then(m => ({
    default: m.LeftBottomFloatingIcons,
  }))
);
const TawkToChat = lazy(() =>
  import("./components/TawkToChat").then(m => ({ default: m.TawkToChat }))
);
const LiveChat = lazy(() =>
  import("./components/LiveChat").then(m => ({ default: m.LiveChat }))
);
const GlobalSafariAddHomeScreen = lazy(() =>
  import("./pages/LandingPage/components/SafariAddHomeScreen").then(m => ({
    default: m.GlobalSafariAddHomeScreen,
  }))
);
const GlobalDownloadAppPopup = lazy(() =>
  import("./components/DownloadAppPopup/GlobalDownloadAppPopup").then(m => ({
    default: m.GlobalDownloadAppPopup,
  }))
);
const GlobalAppDownloadDialog = lazy(() =>
  import("./components/AppDownloadDialog").then(m => ({
    default: m.GlobalAppDownloadDialog,
  }))
);
const GlobalApkUpgradeDialog = lazy(() =>
  import("./components/ApkUpgradeDialog/GlobalApkUpgradeDialog").then(m => ({
    default: m.GlobalApkUpgradeDialog,
  }))
);
const WebPush = lazy(() => import("./components/WebPush"));
const GlobalEvent = lazy(() =>
  import("./components/Event").then(m => ({ default: m.GlobalEvent }))
);
const GlobalRulesDialog = lazy(() =>
  import("./components/RulesDialog").then(m => ({
    default: m.GlobalRulesDialog,
  }))
);
const PrerequisitePopups = lazy(() =>
  import("./components/PrerequisitePopups/PrerequisitePopups").then(m => ({
    default: m.PrerequisitePopups,
  }))
);
// AgreementConfirmation 与 UnpaidGuideController 都不在首屏关键路径
const AgreementConfirmation = lazy(() =>
  import("./components/AgreementConfirmation").then(m => ({
    default: m.AgreementConfirmation,
  }))
);
const UnpaidGuideController = lazy(() =>
  import("./components/UnpaidGuideModal").then(m => ({
    default: m.UnpaidGuideController,
  }))
);
function App() {
  useFavoriteGames();
  useIOSKeyboardFix();
  // 邀请码地址栏持久化:开关开+未登录+有邀请码时,全程(首页/注册页/缓存)保持 ?p=<code>
  useInviteCodeUrlPersist();
  useRecallTracking();

  const [deviceBlacklisted] = useGlobalValue("deviceBlacklisted");
  const { isAuthenticated, userId, username, email } = useUserDataFlattened();
  const customerServices = useCustomerServices();
  // 非关键弹窗：等 load + idle，8s 硬兜底
  const deferredReady = useDeferredMount();
  // 客服 SDK：只有用户真正点了客服才挂载
  const chatMountRequested = useChatMountRequested();
  // 协议确认是否启用：按台子+按国家，来自 startup。startup 未到时为 undefined，
  // 此时不挂载；等它到了再挂。触发器那边会 await startup，不会漏事件。
  // 必须用 hook 订阅，不能用 popupConfig 里的 getXxxEnabled()：那两个是
  // useStartupStore.getState() 的一次性读取，App 不会因 startup 到达而重渲染。
  // 后果很实：启用协议确认的台子上，App 挂载时读到 undefined → 组件不挂载 →
  // 而 triggerAgreementConfirmation 里的 waitAgreementEnabled() 一拿到 startup
  // 就派发事件 —— 此时没有监听者，登录/充值点了没反应。
  // （getXxxEnabled() 留给 triggerAgreementConfirmation 这类模块级非 React 调用方。）
  const { data: startupDataForPopup, loading: startupLoadingForPopup } =
    useStartupData();
  // 三态折叠：字段缺失（老后端还没下发 / startup 失败）时按【启用】回退挂载，
  // 让组件自己打 config 接口定夺 —— 合规闸门只能往安全的一侧失效。
  // 判据集中在 resolvePrerequisiteFlag，与 getXxxEnabled()/waitAgreementEnabled() 同源。
  const startupSettledForPopup =
    !!startupDataForPopup ||
    startupLoadingForPopup === "success" ||
    startupLoadingForPopup === "error";
  const agreementEnabled = resolvePrerequisiteFlag(
    startupDataForPopup?.popupAgreementEnabled,
    startupSettledForPopup
  );
  const ageVerificationEnabled = resolvePrerequisiteFlag(
    startupDataForPopup?.popupAgeVerificationEnabled,
    startupSettledForPopup
  );

  // 获取在线客服配置（必须在条件 return 之前调用，保证 Hooks 数量一致，避免 React #300）
  const onlineServiceConfig = useMemo(() => {
    const onlineService = customerServices.find(
      service => service.customerCategory === "ONLINE_SERVICE"
    );
    if (!onlineService) return null;

    const serviceType = onlineService.serviceType;
    if (serviceType === "TAWK_TO") {
      // TAWK_TO 需要 propertyId 和 widgetId
      if (onlineService.propertyId && onlineService.widgetId) {
        return {
          type: "TAWK_TO" as const,
          propertyId: onlineService.propertyId,
          widgetId: onlineService.widgetId,
        };
      }
    } else if (serviceType === "LIVECHAT") {
      // LIVE_CHAT 需要 licenseId
      if (onlineService.licenseId) {
        return {
          type: "LIVE_CHAT" as const,
          licenseId: onlineService.licenseId,
        };
      }
    }
    return null;
  }, [customerServices]);

  useEffect(() => {
    document.body.setAttribute("data-device", "mobile");

    // 真实 iOS 设备标记（电脑浏览器模拟器不会命中）
    const isRealIOS =
      ["iPad", "iPhone", "iPod"].some(d => navigator.platform?.includes(d)) ||
      (navigator.userAgent.includes("Mac") && "ontouchend" in document);
    if (isRealIOS) {
      document.body.classList.add("is-ios");
    }

    const isRealAndroid =
      /Android/i.test(navigator.userAgent) && "ontouchend" in document;
    if (isRealAndroid) {
      document.body.classList.add("is-android");
    }

    initScreenWidth();

    return () => {
      document.body.removeAttribute("data-device");
      document.body.classList.remove("is-ios");
      document.body.classList.remove("is-android");
    };
  }, []);

  // 设备被拉入黑名单时，仅展示拦截页，不展示当前页面信息（放在所有 Hooks 之后，避免 React #300）
  if (deviceBlacklisted) {
    return <BlockedDevicePage />;
  }

  // 应用主界面
  return (
    <div className="phone-wrapper">
      <AppRouter />
      {/* 批量赠金领取网关：仅在 /activity/batch-bonus-claim 路径触发领取，常驻但零成本 */}
      <BatchBonusClaimGate />
      {/* 客服 SDK：首屏完全不加载。openLiveChat / openTawkChat 被调用时才挂载，
          省掉首屏 24 个请求 / 约 1MB / 5 次跨域握手（widget 本来就是 hidden 的）。 */}
      {chatMountRequested && (
        <Suspense fallback={null}>
          <SilentErrorBoundary>
            {onlineServiceConfig?.type === "TAWK_TO" && (
              <TawkToChat
                propertyId={onlineServiceConfig.propertyId}
                widgetId={onlineServiceConfig.widgetId}
                hideWidget={true}
                user={
                  isAuthenticated
                    ? {
                        userId: userId?.toString(),
                        name: username,
                        email: email,
                      }
                    : undefined
                }
              />
            )}
            {onlineServiceConfig?.type === "LIVE_CHAT" && (
              <LiveChat
                license={onlineServiceConfig.licenseId}
                hideWidget={true}
                user={
                  isAuthenticated
                    ? {
                        userId: userId?.toString(),
                        name: username,
                        email: email,
                      }
                    : undefined
                }
              />
            )}
          </SilentErrorBoundary>
        </Suspense>
      )}
      {/* 这三个原先和客服 SDK 挤在同一个块里，但它们与客服无关，必须无条件挂载：
          GlobalRulesDialog / AgreementConfirmation 都是事件驱动（后者监听
          show-agreement-confirmation，由 Login/Recharge 触发），不挂载就等于该弹的
          框永远不弹。所以走 deferredReady（带硬超时兜底），不能跟着客服走按需挂载。 */}
      {deferredReady && (
        <Suspense fallback={null}>
          <SilentErrorBoundary>
            <GlobalSafariAddHomeScreen />
            <GlobalRulesDialog />
          </SilentErrorBoundary>
        </Suspense>
      )}
      {/* AgreementConfirmation：事件模式，监听 show-agreement-confirmation。
          启用时不能挂在 deferredReady 后面 —— Login.handleLogin /
          Recharge.proceedDepositFlow 把整个后续流程都放在该事件的回调里，
          闸门最长要 8s 才放行，这段时间内点登录 = 事件没人接 = 按钮没反应。
          未启用时整块不渲染，lazy chunk 不 fetch、CSS 不进首屏（此时由
          triggerAgreementConfirmation 直接放行，不依赖监听器）。
          启用状态来自 startup，按台子+按国家，见 config/popupConfig.ts */}
      {agreementEnabled && (
        <Suspense fallback={null}>
          <SilentErrorBoundary>
            <AgreementConfirmation />
          </SilentErrorBoundary>
        </Suspense>
      )}
      <Suspense fallback={null}>
        {/* PopupQueueProvider 和 FloatingIconsProvider 立即挂载：
            LeftBottomFloatingIcons 依赖 FloatingIconsContext 的真实上下文，
            否则会走 useFloatingIconsContext 的空 fallback，丢失首屏悬浮图标。
            两个 Provider 自身比较轻；真正重的 PrerequisitePopups / WebPush /
            Global* / GlobalEvent 等弹窗组件 lazy chunk 由 deferredReady 网关
            延迟到首屏资源落地后再触发下载与挂载（带硬超时兜底，保证一定会挂载）。 */}
        {/* 队列默认不暂停：暂停由 PrerequisitePopups 在「确认要做年龄验证」时自己 pause。
            旧写法是 initialPaused 跟开关联动，而唯一的 resume 也在那个组件里 ——
            两者一旦不同步（比如开关关了组件就不渲染），全站弹窗会被永久阻塞。 */}
        <PopupQueueProvider>
          <FloatingIconsProvider>
            <SilentErrorBoundary>
              <LeftBottomFloatingIcons />
            </SilentErrorBoundary>
            {/* 只在【确认启用】时挂载：未启用（含 startup 未到）时连 lazy chunk 都不下载，
                首屏零成本 —— 这是本轮提速的一部分，不能因为改成运行时判定就丢掉。
                之所以敢这么写，是因为队列已经不再 initialPaused：暂停只由本组件在
                确认要做年龄验证时发起，所以"没人 resume"这个失效模式已不存在。
                不放 deferredReady 后面：年龄验证是合规闸门，最长等 8s 才出现意味着
                这段时间用户已经能操作了。外层已有 Suspense（见上），lazy 无需再包。 */}
            {ageVerificationEnabled && (
              <SilentErrorBoundary>
                <PrerequisitePopups />
              </SilentErrorBoundary>
            )}
            {deferredReady && (
              <SilentErrorBoundary>
                <WebPush />
                <GlobalDownloadAppPopup />
                <GlobalAppDownloadDialog />
                <GlobalApkUpgradeDialog />
                <GlobalEvent
                // firstRechargeVisible={false}
                // cashbackVisible={false}
                // rechargeOfferVisible={false}
                // redPacketVisible={false}
                // rescueFundVisible={false}
                />
              </SilentErrorBoundary>
            )}
          </FloatingIconsProvider>
        </PopupQueueProvider>
      </Suspense>
      {/* UnpaidGuideController：shouldRun = isAuthenticated && enableLastPaymentVoucher，
          且首次 mount 不会立即弹（hasCheckedNewRef 双重保护），
          只对挂载后新增的订单弹窗——首屏 1-3s 内不会有"新增订单"，可安全延迟 */}
      {deferredReady && (
        <Suspense fallback={null}>
          <SilentErrorBoundary>
            <UnpaidGuideController />
          </SilentErrorBoundary>
        </Suspense>
      )}
    </div>
  );
}

export default App;
